<template>
  <button type="button" class="filters-btn" @click="open">
    <font-awesome-icon :icon="['fas', 'filter']" />
    <span>Filters</span>
    <span v-if="activeCount > 0" class="filters-btn__count">{{ activeCount }}</span>
  </button>

  <el-dialog v-model="visible" title="Filter artworks" width="560px">
    <div class="filter-group">
      <label class="filter-group__label">Artist</label>
      <el-select v-model="draft.artists" multiple filterable clearable placeholder="All artists" size="small"
        style="width: 100%">
        <el-option v-for="artist in artists" :key="artist.id" :label="artist.name" :value="artist.id" />
      </el-select>
    </div>

    <div class="filter-group">
      <label class="filter-group__label">Status</label>
      <el-radio-group v-model="draft.status" size="small">
        <el-radio-button label="">All</el-radio-button>
        <el-radio-button label="published">Published</el-radio-button>
        <el-radio-button label="draft">Draft</el-radio-button>
      </el-radio-group>
    </div>

    <div class="filter-group">
      <label class="filter-group__label">Stock</label>
      <el-radio-group v-model="draft.stock" size="small">
        <el-radio-button label="">All</el-radio-button>
        <el-radio-button label="available">Not sold out</el-radio-button>
        <el-radio-button label="sold_out">Sold out</el-radio-button>
      </el-radio-group>
    </div>

    <div class="filter-group">
      <label class="filter-group__label">Size</label>
      <div class="filter-group__checks">
        <el-checkbox :model-value="allSizesSelected" @change="toggleAllSizes">All</el-checkbox>
        <el-checkbox-group v-model="draft.sizeTypes">
          <el-checkbox v-for="size in sizes" :key="size.id" :label="size.id">{{ size.title }}</el-checkbox>
        </el-checkbox-group>
      </div>
    </div>

    <div class="filter-group">
      <label class="filter-group__label">Technique</label>
      <el-select v-model="draft.techniques" multiple filterable clearable placeholder="All techniques" size="small"
        style="width: 100%">
        <el-option v-for="technique in techniques" :key="technique.id" :label="technique.label"
          :value="technique.id" />
      </el-select>
    </div>

    <div class="filter-group">
      <label class="filter-group__label">Price ($)</label>
      <div class="filter-group__price">
        <el-input v-model="draft.priceMin" size="small" type="number" min="0" placeholder="Min" />
        <span>–</span>
        <el-input v-model="draft.priceMax" size="small" type="number" min="0" placeholder="Max" />
      </div>
    </div>

    <template #footer>
      <div class="filters-footer">
        <el-button size="small" @click="clear">Clear all</el-button>
        <div>
          <el-button size="small" @click="visible = false">Cancel</el-button>
          <el-button size="small" type="primary" @click="apply">Apply filters</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
export const emptyArtworkFilters = () => ({
  artists: [],
  status: "",
  stock: "",
  sizeTypes: [],
  techniques: [],
  priceMin: "",
  priceMax: "",
});

// Turns the filter state into the query params the artworks index understands.
export const artworkFilterParams = (filters, sizes) => {
  const params = {};
  if (filters.artists.length) params.artist = filters.artists.join(",");
  if (filters.status) params.status = filters.status;
  if (filters.stock) params.stock = filters.stock;
  // Every size ticked is the same as no size filter, and also keeps artworks without a size.
  if (filters.sizeTypes.length && filters.sizeTypes.length < sizes.length) {
    params.size_type = filters.sizeTypes.join(",");
  }
  if (filters.techniques.length) params.techniques = filters.techniques.join(",");
  if (filters.priceMin !== "") params["price_range[min]"] = filters.priceMin;
  if (filters.priceMax !== "") params["price_range[max]"] = filters.priceMax;
  return params;
};

export default {
  props: {
    modelValue: { type: Object, required: true },
    sizes: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      visible: false,
      draft: emptyArtworkFilters(),
      artists: [],
      techniques: [],
      optionsLoaded: false,
    };
  },
  computed: {
    activeCount() {
      const f = this.modelValue;
      const sizeActive = f.sizeTypes.length > 0 && f.sizeTypes.length < this.sizes.length;
      return [
        f.artists.length > 0,
        f.status !== "",
        f.stock !== "",
        sizeActive,
        f.techniques.length > 0,
        f.priceMin !== "" || f.priceMax !== "",
      ].filter(Boolean).length;
    },
    allSizesSelected() {
      return this.sizes.length > 0 && this.draft.sizeTypes.length === this.sizes.length;
    },
  },
  methods: {
    open() {
      this.draft = JSON.parse(JSON.stringify(this.modelValue));
      this.visible = true;
      this.loadOptions();
    },
    toggleAllSizes(checked) {
      this.draft.sizeTypes = checked ? this.sizes.map((s) => s.id) : [];
    },
    clear() {
      this.draft = emptyArtworkFilters();
    },
    apply() {
      this.$emit("update:modelValue", this.draft);
      this.visible = false;
    },
    async loadOptions() {
      if (this.optionsLoaded) return;
      const api = process.env.VUE_APP_SHARED_API;
      try {
        const [artists, techniques] = await Promise.all([
          this.$api.get(`${api}api/artists?per_page=1000`),
          this.$api.get(`${api}api/techniques?per_page=1000`),
        ]);
        this.artists = (artists.data.data || [])
          .map(({ id, name }) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.techniques = (techniques.data.data || []).flatMap((parent) => [
          { id: parent.id, label: parent.name },
          ...(parent.child || []).map((child) => ({
            id: child.id,
            label: `${parent.name} › ${child.name}`,
          })),
        ]);
        this.optionsLoaded = true;
      } catch (_) { }
    },
  },
};
</script>

<style lang="scss" scoped>
.filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  margin-right: 8px;
  background: transparent;
  border: 1px solid #1f3c2a;
  border-radius: 3px;
  color: #1f3c2a;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(31, 60, 42, 0.06);
  }
}

.filters-btn__count {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background-color: #338973;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.filter-group {
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }
}

.filter-group__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f3c2a;
}

.filter-group__checks {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 30px;

  :deep(.el-checkbox-group) {
    flex: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0 30px;
  }

  :deep(.el-checkbox) {
    margin-right: 0;
  }
}

.filter-group {
  :deep(.el-radio-button.is-active .el-radio-button__inner) {
    background-color: #1f3c2a;
    border-color: #1f3c2a;
    box-shadow: -1px 0 0 0 #1f3c2a;
    color: #fff;
  }

  :deep(.el-radio-button__inner:hover) {
    color: #338973;
  }

  :deep(.el-radio-button.is-active .el-radio-button__inner:hover) {
    color: #fff;
  }

  :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #1f3c2a;
    border-color: #1f3c2a;
  }

  :deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
    color: #1f3c2a;
  }
}

.filter-group__price {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filters-footer {
  display: flex;
  justify-content: space-between;
}
</style>
